import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { LogOut, Bookmark, ChevronLeft, ChevronRight } from "lucide-react";

export const PhaseLayout = ({ 
  children, 
  phaseNumber, 
  phaseName, 
  phaseSubtitle,
  phaseDescription,
  days,
  nextPhase,
  prevPhase 
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const getInitials = (name) => {
    if (!name) return "CS";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-warm border-b border-[#E5E0D6]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/journey" 
              className="flex items-center gap-2 text-[#2C2C2C]/60 hover:text-[#6B8E7C] transition-colors"
              data-testid="back-to-journey"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Blueprint</span>
            </Link>
            <span className="text-[#E5E0D6]">|</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#D4A373] font-medium uppercase tracking-wide">
                Phase {phaseNumber}
              </span>
              <span className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                {phaseName}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/saved" 
              className="flex items-center gap-2 text-[#2C2C2C]/70 hover:text-[#6B8E7C] transition-colors"
              data-testid="saved-link"
            >
              <Bookmark className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Saved</span>
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="phase-user-menu">
                  <Avatar className="h-10 w-10 border-2 border-[#E5E0D6]">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#6B8E7C] text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border-[#E5E0D6]" align="end">
                <div className="px-3 py-2 border-b border-[#E5E0D6]">
                  <p className="text-sm font-medium text-[#2C2C2C]">{user?.name}</p>
                  <p className="text-xs text-[#2C2C2C]/50">{user?.email}</p>
                </div>
                <DropdownMenuItem 
                  onClick={logout}
                  className="cursor-pointer text-[#2C2C2C]/70 hover:text-[#2C2C2C] focus:bg-[#F2F0EB]"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Phase Header */}
          <div className="mb-12 text-center animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4A373]/10 rounded-full mb-6">
              <span className="text-xs text-[#D4A373] font-medium uppercase tracking-wide">
                Phase {phaseNumber}
              </span>
              <span className="text-xs text-[#2C2C2C]/30">•</span>
              <span className="text-xs text-[#2C2C2C]/50">{days}</span>
            </div>
            <h1 className="text-4xl md:text-5xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              {phaseName}
            </h1>
            <p className="text-[#6B8E7C] text-sm font-medium mb-4">
              {phaseSubtitle}
            </p>
            <p className="text-[#2C2C2C]/60 text-lg font-light max-w-xl mx-auto">
              {phaseDescription}
            </p>
          </div>

          {/* Steps */}
          {children}

          {/* Phase Navigation */}
          <div className="mt-16 pt-8 border-t border-[#E5E0D6] flex items-center justify-between">
            {prevPhase ? (
              <button
                onClick={() => navigate(prevPhase.path)}
                className="flex items-center gap-2 text-[#2C2C2C]/50 hover:text-[#6B8E7C] transition-colors"
                data-testid="prev-phase-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Phase {prevPhase.number}: {prevPhase.name}</span>
              </button>
            ) : (
              <div />
            )}
            
            {nextPhase ? (
              <button
                onClick={() => navigate(nextPhase.path)}
                className="flex items-center gap-2 text-[#6B8E7C] hover:text-[#5A7A6A] transition-colors"
                data-testid="next-phase-btn"
              >
                <span className="text-sm font-medium">Phase {nextPhase.number}: {nextPhase.name}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <Link 
                to="/journey"
                className="flex items-center gap-2 text-[#6B8E7C] hover:text-[#5A7A6A] transition-colors"
              >
                <span className="text-sm font-medium">View Your Blueprint</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
